/**
 * Findings, one row per file (release 2.1 Gate A items 5, 6, 8, 9, 11).
 *
 * A spreadsheet holding PAN, Aadhaar and account numbers for thousands of
 * people is ONE row: its name, where it is, the laptop and its user, every
 * data type with roughly how many values, one severity. Forty daily exports
 * in one folder are one row too. Expand a row for each file, each data type,
 * the sheet and column it sat in, and the masked proof.
 *
 * Rows come from the server (/v1/discovery/findings/by-file), so the counts
 * cover every matching finding, not the page on screen.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Clock, EyeOff, FileText, Laptop, Loader2, Search, ShieldCheck, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TIER_BADGE, TIER_HINT, TIER_LABEL, type FindingTier } from '@/lib/finding-tier';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import {
  listDiscoveryFindingFileGroups,
  listDiscoveryFindingsInFileGroup,
  type DiscoveryFileGroup,
  type DiscoveryFileGroupPage,
  type DiscoveryFileGroupQuery,
  type DiscoveryFinding,
} from '@/lib/api';
import {
  columnLine,
  dataTypeLabel,
  estimateBasis,
  fileNameOf,
  formatEstimate,
  groupLocation,
  groupOwner,
  groupSize,
  groupTitle,
  matchInWords,
  type FileColumnHit,
} from './findings-by-file-format';

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/** The slice of a console finding this view reads. */
export interface FileViewFinding {
  id: string;
  type: string;
  severity: Severity;
  status: string;
  asset: string;
  pii: string[];
  maskedSamples: string[];
  matchedCount: number | null;
  samplesCount: number | null;
  localPath: string | null;
  deviceUser: string | null;
  estimatedCount: number | null;
  rowsInFile: number | null;
  fileColumns: FileColumnHit[];
  /** Release 3.3 B4: the product tier, shown beside the data type. */
  tier?: FindingTier;
}

const PAGE_SIZE = 50;
const FILES_SHOWN = 5;

/** The console's status names -> the server's. Acknowledged and in remediation are both in_progress. */
export const SERVER_STATUS: Record<string, string | undefined> = {
  all: undefined,
  open: 'open',
  escalated: 'escalated',
  acknowledged: 'in_progress',
  in_remediation: 'in_progress',
  accepted_risk: 'accepted_risk',
  resolved: 'resolved',
  false_positive: 'false_positive',
};

function sevVariant(s: string): 'critical' | 'high' | 'medium' | 'low' | 'glass' {
  return s === 'critical' || s === 'high' || s === 'medium' || s === 'low' ? s : 'glass';
}

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${Math.max(m, 0)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

interface GroupDetail {
  loading: boolean;
  error: string | null;
  findings: FileViewFinding[];
  truncated: boolean;
}

export function FindingsByFile<F extends FileViewFinding>({
  severity,
  status,
  search,
  review,
  adapt,
  onOpenFinding,
  onSetStatus,
  refreshToken,
}: {
  severity: 'all' | Severity;
  status: string;
  search: string;
  review: boolean;
  adapt: (dto: DiscoveryFinding) => F;
  onOpenFinding: (f: F) => void;
  /** Change the status of every finding in a row; resolves when done. */
  onSetStatus: (ids: string[], serverStatus: DiscoveryFinding['status'], note: string) => Promise<void>;
  /** Bump to reload after a change made elsewhere on the page. */
  refreshToken: number;
}) {
  const [includeWeak, setIncludeWeak] = useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<DiscoveryFileGroupPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<Map<string, GroupDetail>>(new Map());
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [debounced, setDebounced] = useState(search);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const query = useMemo<DiscoveryFileGroupQuery>(
    () => ({
      ...(severity !== 'all' ? { severity } : {}),
      ...(SERVER_STATUS[status] ? { status: SERVER_STATUS[status] } : {}),
      tier: review ? 'review' : 'confirmed',
      includeWeak,
      search: debounced,
    }),
    [severity, status, review, includeWeak, debounced],
  );

  // A new filter starts from the first page.
  useEffect(() => {
    setOffset(0);
  }, [query]);

  const findingsFrom = useRef(adapt);
  findingsFrom.current = adapt;

  const loadDetail = (key: string): void => {
    setDetails((prev) => new Map(prev).set(key, { loading: true, error: null, findings: prev.get(key)?.findings ?? [], truncated: false }));
    listDiscoveryFindingsInFileGroup(key, query)
      .then((r) =>
        setDetails((prev) =>
          new Map(prev).set(key, {
            loading: false,
            error: null,
            findings: r.findings.map((d) => findingsFrom.current(d)),
            truncated: r.truncated,
          }),
        ),
      )
      .catch((e: unknown) =>
        setDetails((prev) =>
          new Map(prev).set(key, {
            loading: false,
            error: e instanceof Error ? e.message : 'Could not load this row',
            findings: [],
            truncated: false,
          }),
        ),
      );
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    listDiscoveryFindingFileGroups({ ...query, limit: PAGE_SIZE, offset })
      .then((p) => {
        if (!live) return;
        setPage(p);
        setError(null);
        // Rows the reviewer has open stay open, with fresh contents.
        setDetails(new Map());
        for (const key of expanded) if (p.groups.some((g) => g.key === key)) loadDetail(key);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof Error ? e.message : 'Could not load findings by file');
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // `expanded` is read, not a trigger: opening a row must not refetch the page.
  }, [query, offset, refreshToken, retry]);

  const toggle = (key: string): void => {
    const opening = !expanded.has(key);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (opening) next.add(key);
      else next.delete(key);
      return next;
    });
    if (opening && !details.has(key)) loadDetail(key);
  };

  const setStatusForRow = async (key: string, serverStatus: DiscoveryFinding['status'], note: string): Promise<void> => {
    const d = details.get(key);
    if (!d || d.findings.length === 0) return;
    setBusyKey(key);
    try {
      await onSetStatus(d.findings.map((f) => f.id), serverStatus, note);
    } finally {
      setBusyKey(null);
    }
  };

  const total = page?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
        <span>
          {loading && !page ? 'Loading files…' : `${from.toLocaleString('en-IN')}-${to.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')} files and assets`}
          {review ? ' · possible (not in headline counts)' : ' · confirmed or likely'}
        </span>
        <button
          type="button"
          onClick={() => setIncludeWeak((v) => !v)}
          aria-pressed={includeWeak}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 hover:bg-muted/50 text-foreground/80"
          title="A weak match is fewer than 1 in 10 sampled values, e.g. one stray value in a file"
        >
          <EyeOff className="h-3 w-3" />
          {includeWeak
            ? 'Hide weak matches'
            : `Show weak matches${page && page.hiddenWeak > 0 ? ` (${page.hiddenWeak.toLocaleString('en-IN')} hidden)` : ''}`}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-sev-high/40 p-3 text-xs flex items-center justify-between gap-2">
          <span>{error}</span>
          <Button size="sm" variant="glass" onClick={() => setRetry((n) => n + 1)}>Retry</Button>
        </div>
      )}

      <GlassCard className="p-0 overflow-hidden">
        {loading && !page ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading files…
          </div>
        ) : page && page.groups.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No files match these filters</div>
            {!includeWeak && page.hiddenWeak > 0 && (
              <div className="mt-1">{page.hiddenWeak.toLocaleString('en-IN')} weak matches are hidden.</div>
            )}
          </div>
        ) : (
          <ul className={cn('divide-y divide-border/40', loading && 'opacity-60')}>
            {page?.groups.map((g) => (
              <FileRow
                key={g.key}
                group={g}
                open={expanded.has(g.key)}
                detail={details.get(g.key)}
                busy={busyKey === g.key}
                onToggle={() => toggle(g.key)}
                onOpenFinding={(f) => onOpenFinding(f as F)}
                onSetStatus={(s, note) => void setStatusForRow(g.key, s, note)}
              />
            ))}
          </ul>
        )}
      </GlassCard>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 px-1 text-xs">
          <Button variant="glass" size="sm" disabled={offset === 0 || loading} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
            ← Previous
          </Button>
          <Button variant="glass" size="sm" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}

function TypeChip({ label, estimated, severity }: { label: string; estimated: number | null; severity: string }) {
  const est = formatEstimate(estimated);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-4 whitespace-nowrap',
        severity === 'critical' || severity === 'high'
          ? 'border-sev-high/40 bg-sev-high/10 text-foreground'
          : 'border-border bg-muted/40 text-foreground/90',
      )}
    >
      <span className="font-medium">{dataTypeLabel(label)}</span>
      {est && <span className="text-muted-foreground tabular-nums">{est}</span>}
    </span>
  );
}

function FileRow({
  group: g,
  open,
  detail,
  busy,
  onToggle,
  onOpenFinding,
  onSetStatus,
}: {
  group: DiscoveryFileGroup;
  open: boolean;
  detail: GroupDetail | undefined;
  busy: boolean;
  onToggle: () => void;
  onOpenFinding: (f: FileViewFinding) => void;
  onSetStatus: (serverStatus: DiscoveryFinding['status'], note: string) => void;
}) {
  const owner = groupOwner(g);
  const location = groupLocation(g);
  const shownTypes = g.types.slice(0, 8);
  return (
    <li className={cn(g.severity === 'critical' && 'bg-sev-critical/5')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        {open ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
        <Badge variant={sevVariant(g.severity)} className="shrink-0 mt-0.5 capitalize">{g.severity}</Badge>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-medium text-sm text-foreground break-all">{groupTitle(g)}</span>
            <span className="text-[11px] text-muted-foreground">{groupSize(g)}</span>
          </div>
          <div className="font-mono text-[11px] text-muted-foreground break-all" title={location}>{location}</div>
          <div className="flex flex-wrap gap-1">
            {shownTypes.map((t) => (
              <TypeChip key={t.label} label={t.label} estimated={t.estimated} severity={t.severity} />
            ))}
            {g.types.length > shownTypes.length && (
              <span className="text-[11px] text-muted-foreground self-center">+{g.types.length - shownTypes.length} more</span>
            )}
          </div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-1 shrink-0 text-[11px] text-muted-foreground w-48">
          {g.device && (
            <span className="inline-flex items-center gap-1 text-foreground/90"><Laptop className="h-3 w-3" />{g.device}</span>
          )}
          {owner && <span className="inline-flex items-center gap-1 truncate max-w-full"><User className="h-3 w-3 shrink-0" />{owner}</span>}
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{ago(g.lastSeenAt)}</span>
          <span>{g.openFindings === g.findings ? `${g.findings} open` : `${g.openFindings} of ${g.findings} open`}</span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-10">
          <FileRowDetail group={g} detail={detail} busy={busy} onOpenFinding={onOpenFinding} onSetStatus={onSetStatus} />
        </div>
      )}
    </li>
  );
}

function FileRowDetail({
  group: g,
  detail,
  busy,
  onOpenFinding,
  onSetStatus,
}: {
  group: DiscoveryFileGroup;
  detail: GroupDetail | undefined;
  busy: boolean;
  onOpenFinding: (f: FileViewFinding) => void;
  onSetStatus: (serverStatus: DiscoveryFinding['status'], note: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  if (!detail || (detail.loading && detail.findings.length === 0)) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading what was found…
      </div>
    );
  }
  if (detail.error) return <div className="py-2 text-xs text-sev-high">{detail.error}</div>;

  // One block per file in the row; a single file is one block.
  const byFile = new Map<string, FileViewFinding[]>();
  for (const f of detail.findings) {
    const where = f.localPath ?? f.asset;
    const list = byFile.get(where);
    if (list) list.push(f);
    else byFile.set(where, [f]);
  }
  const files = [...byFile.entries()];
  const visibleFiles = showAll ? files : files.slice(0, FILES_SHOWN);

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
      {visibleFiles.map(([where, list]) => (
        <FileBlock key={where} where={where} findings={list} multi={g.files > 1} onOpenFinding={onOpenFinding} />
      ))}
      {files.length > FILES_SHOWN && (
        <button type="button" className="text-xs text-primary hover:underline" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer files' : `Show all ${files.length} files`}
        </button>
      )}
      {detail.truncated && (
        <p className="text-[11px] text-muted-foreground">Showing the 500 most severe findings in this row.</p>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/50">
        <span className="text-[11px] text-muted-foreground mr-1">
          All {detail.findings.length} finding{detail.findings.length === 1 ? '' : 's'} in this row:
        </span>
        <Button variant="glass" size="sm" disabled={busy} onClick={() => onSetStatus('in_progress', 'Acknowledged for the whole file')} data-action="disc.dspm_findings.a11">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />} Acknowledge
        </Button>
        <Button variant="glass" size="sm" disabled={busy} onClick={() => onSetStatus('resolved', 'Resolved for the whole file')} data-action="disc.dspm_findings.a11">
          Resolve
        </Button>
        <Button variant="glass" size="sm" disabled={busy} onClick={() => onSetStatus('false_positive', 'Marked false positive for the whole file')} data-action="disc.dspm_findings.a11">
          False positive
        </Button>
      </div>
    </div>
  );
}

function FileBlock({
  where,
  findings,
  multi,
  onOpenFinding,
}: {
  where: string;
  findings: FileViewFinding[];
  multi: boolean;
  onOpenFinding: (f: FileViewFinding) => void;
}) {
  const first = findings[0];
  if (!first) return null;
  const rows = findings.find((f) => f.rowsInFile !== null)?.rowsInFile ?? null;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <FileText className="h-3.5 w-3.5 self-center text-muted-foreground" />
        {multi && <span className="font-medium">{fileNameOf(where)}</span>}
        <code className="font-mono text-[11px] text-muted-foreground break-all">{where}</code>
        {rows !== null && <span className="text-[11px] text-muted-foreground">{rows.toLocaleString('en-IN')} rows</span>}
        {first.deviceUser && <span className="text-[11px] text-muted-foreground">· {first.deviceUser}</span>}
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {findings.map((f) => {
          const label = dataTypeLabel(f.pii[0] ?? f.type);
          const est = formatEstimate(f.estimatedCount);
          const words = matchInWords(f.matchedCount, f.samplesCount);
          return (
            <Fragment key={f.id}>
              <div className="rounded border border-border/50 bg-card px-2.5 py-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={sevVariant(f.severity)} className="text-[9px] capitalize">{f.severity}</Badge>
                  <span className="font-medium">{label}</span>
                  {f.tier && (
                    <Badge variant={TIER_BADGE[f.tier]} className="text-[9px]" title={TIER_HINT[f.tier]} data-tier={f.tier}>
                      {TIER_LABEL[f.tier].toLowerCase()}
                    </Badge>
                  )}
                  {est && <span className="tabular-nums">{est} values</span>}
                  {words && <span className="text-muted-foreground">{words}</span>}
                  {f.status !== 'open' && <span className="text-muted-foreground capitalize">· {f.status.replace(/_/g, ' ')}</span>}
                  <button
                    type="button"
                    className="ml-auto text-primary hover:underline"
                    onClick={() => onOpenFinding(f)}
                  >
                    Open
                  </button>
                </div>
                <div className="mt-1 space-y-1">
                  {f.fileColumns.length > 0 && (
                    <ul className="text-[11px] text-foreground/90 space-y-0.5">
                      {f.fileColumns.map((c) => (
                        <li key={`${c.sheet ?? ''}>${c.column}`} className="font-mono">{columnLine(c)}</li>
                      ))}
                    </ul>
                  )}
                  {est && (
                    <p className="text-[11px] text-muted-foreground">{estimateBasis(f.fileColumns, f.rowsInFile)}</p>
                  )}
                  {f.maskedSamples.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {f.maskedSamples.slice(0, 4).map((s) => (
                        <code key={s} className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                          {s}
                        </code>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
